import { TwilioConfigInput } from '@pypestream/api-services/urql';

// Imported from the module, not the `utils` barrel: the barrel drags in the
// XState graph and with it `api-services`, which this package's vitest setup
// stubs — a barrel import makes this builder untestable in isolation.
import { encodeToBase64 } from '../../utils/encode-base64';
import { resolveConversationHandling } from './flux-rules';
import {
  encodeLanguagesConfig,
  LANGUAGE_TTS_PROVIDER,
} from './languages-config';
import type { VoiceChannelFormValues } from './voice-channel-settings-form';

/** The Platform select's value for a Pypestream Contact Center voice inbox. */
export const CONTACT_CENTER_PLATFORM = 'ContactCenter';

type FlagAccess = 'allowed' | 'denied' | 'pending';

/**
 * PSTUDIO-4684: the voice inbox is Bobcat work, so the Platform select offers
 * it only under the master flag (`fiveStageNav`) — with it off the form is
 * production's. A channel already saved with it keeps showing its value rather
 * than being silently switched to another platform.
 */
export const offersVoiceInbox = (
  masterFlag: FlagAccess,
  platform: string | undefined
): boolean => masterFlag === 'allowed' || platform === CONTACT_CENTER_PLATFORM;

/** Saving connects the inbox and writes escalation rows: master flag only. */
export const setsUpVoiceInbox = (masterFlag: FlagAccess): boolean =>
  masterFlag === 'allowed';

/** Everything the payload needs; `botName` and `env` are channel-level, not config. */
export type VoiceAiConfigFormData = Omit<
  VoiceChannelFormValues,
  'botName' | 'env'
>;

/**
 * Builds a Voice AI channel's complete `twilio_params` payload.
 *
 * Extracted from the form (the way `resolveConversationHandling` already was)
 * so the one rule that is easy to break silently can be tested: this payload
 * REBUILDS THE WHOLE MAP, and the Hasura request transform nulls every key the
 * client omits. A field dropped here is not a missing field — it is that
 * setting being cleared on the next save of anything else (PSTUDIO-4090).
 *
 * Since PSTUDIO-2950 the voice comes from the Languages block. The flat
 * `language` / `elevenlabs*` keys describe the STARTING language: they are what
 * a call runs on whenever the overlay is absent or rejected, so they must stay
 * correct even when `languagesConfigB64` carries every language.
 *
 * The return type makes `enableCallRecording` required, which the generated
 * `TwilioConfigInput` does not, so the omission at least reads as intentional.
 * Note that it is NOT a build gate: this package's `tsc` is a solution file
 * with `files: []`, so no typecheck runs. The tests are the gate.
 */
export const buildVoiceAiConfig = (
  formData: VoiceAiConfigFormData
): TwilioConfigInput & { enableCallRecording: boolean } => {
  // Validation keeps the starting key among the entries; the fallback only
  // guarantees the payload never goes out without a flat language at all.
  const starting =
    formData.languages.find((l) => l.key === formData.startingLanguage) ??
    formData.languages[0];

  const encodedMetadata = starting?.elevenlabsVoiceMetadata
    ? encodeToBase64(JSON.stringify(starting.elevenlabsVoiceMetadata))
    : null;

  const isContactCenter = formData.laPlatform === CONTACT_CENTER_PLATFORM;
  const hasLaConfig =
    formData.laPlatform ||
    formData.laClientId ||
    formData.laClientSecret ||
    formData.laGrantType ||
    formData.laInstanceUrl ||
    formData.laOAuthUrl;

  // A Contact Center voice inbox is addressed by account and inbox alone —
  // the same shape the engine reads for `callType: voice_inbox`.
  const encodedLaConfig = isContactCenter
    ? encodeToBase64(
        JSON.stringify({
          account_id: formData.laAccountId.trim(),
          voice_inbox_id: formData.laVoiceInboxId.trim(),
        })
      )
    : hasLaConfig
      ? encodeToBase64(
          JSON.stringify({
            integrator: formData.laPlatform,
            client_id: formData.laClientId,
            client_secret: formData.laClientSecret,
            grant_type: formData.laGrantType,
            instance_url: formData.laInstanceUrl,
            o_auth_url: formData.laOAuthUrl,
          })
        )
      : null;

  const handoffRules = formData.handoffRules?.filter(
    (r) => r.skillTag && r.destination
  );
  const encodedHandoffOptions =
    handoffRules?.length > 0
      ? encodeToBase64(
          JSON.stringify(
            Object.fromEntries(
              handoffRules.map(
                ({ skillTag, destination, type: handoffType }) => [
                  skillTag,
                  // The Type select SHOWS "Phone number" when the rule's type
                  // is unset, but never writes it to the form — so a rule saved
                  // without touching it went out with no `type` at all.
                  { destination, type: handoffType ?? 'phone_number' },
                ]
              )
            )
          )
        )
      : null;

  const conversationHandling = resolveConversationHandling({
    speechModel: formData.speechModel,
    inboundMessageBuffer: formData.inboundMessageBuffer,
    userSilenceTimeout: formData.userSilenceTimeout,
    eotThreshold: formData.eotThreshold,
  });

  return {
    accountSid: formData.accountSid,
    authToken: formData.authToken,
    language: starting?.locale,
    phoneNumber: formData.phoneNumber,
    speechModel: formData.speechModel,
    transcriptionProvider: formData.transcriptionProvider,
    ttsProvider: LANGUAGE_TTS_PROVIDER,
    elevenlabsAudioModel: starting?.elevenlabsAudioModel,
    elevenlabsVoiceId: starting?.elevenlabsVoiceId,
    elevenlabsSimilarity: String(starting?.elevenlabsSimilarity),
    elevenlabsSpeed: String(starting?.elevenlabsSpeed),
    elevenlabsStability: String(starting?.elevenlabsStability),
    elevenlabsVoiceMetadataB64: encodedMetadata,
    // Always sent, null included: like every key here it is cleared when
    // omitted, and an explicit null is what drops a stale overlay.
    languagesConfigB64: encodeLanguagesConfig(
      formData.languages,
      formData.startingLanguage
    ),
    metadataIntegrationInfoB64: encodedLaConfig,
    voiceHandoffOptionsB64: encodedHandoffOptions,
    userSilenceTimeout: conversationHandling.userSilenceTimeout,
    inboundMessageBufferMs: conversationHandling.inboundMessageBufferMs,
    // TODO(PSTUDIO-3630): send conversationHandling.eotThreshold once the
    // backend exposes TwilioConfigInput.eotThreshold. GraphQL rejects an entire
    // mutation containing an unknown input field, so sending it early would
    // break every voice channel save, not just this setting.
    promptMaxRetries: formData.promptMaxRetries,
    retryPromptPrefix: formData.retryPromptPrefix,
    // Always sent, touched or not — see VoiceChannelFormValues. Coerced
    // because `initialValues` is Partial: a caller omitting the key would
    // otherwise drop it from the JSON and reintroduce the same bug.
    enableCallRecording: formData.enableCallRecording === true,
  };
};
